import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { ListingHeader } from "../../../components/listing-header";
import Loader from "../../../components/designs/loader";
import axiosService from "../../../core/services/axios-service";
import { ApiRoutes } from "../../../core/dictionary/api-url";

export default function CoachTeamMemberDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [courses, setCourses] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axiosService.get(ApiRoutes.ASSIGNED_COURSES_BY_USER);
        const mapped = (res?.data?.data || []).map((item) => ({
          _id: item.course._id,
          name: item.course.courseName,
          thumbnail: item.course.imageUrl,
          progress: item.coursePercentage ?? 0,
        }));
        setCourses(mapped);
      } catch (err) {
        setError("Failed to load courses. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  return (
    <>
      <ListingHeader
        title="My Courses"
        breadCrumb={[{ label: "Dashboard", href: "/dashboard" }]}
        actions={[]}
        dropdown={null}
      />

      {loading ? (
        <div className="course-loader-wrapper">
          <Loader text="Loading your courses..." />
        </div>
      ) : error ? (
        <div className="course-dashboard-section">
          <p className="text-danger">{error}</p>
        </div>
      ) : courses.length === 0 ? (
        <div className="course-dashboard-empty">
          <div className="course-dashboard-empty__icon">
            <BookOpen size={48} strokeWidth={1.5} />
          </div>
          <p className="course-dashboard-empty__title">No courses assigned yet</p>
          <p className="course-dashboard-empty__sub">
            Your assigned courses will appear here once they are added.
          </p>
        </div>
      ) : (
        <div className="course-dashboard-section">
          <div className="course-grid">
            {courses.map((course) => {
              const progress = Math.min(100, Math.max(0, course.progress ?? 0));
              return (
                <div
                  key={course._id}
                  className="course-card"
                  onClick={() => navigate(`/course/${course._id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && navigate(`/course/${course._id}`)
                  }
                >
                  {course.thumbnail ? (
                    <img
                      src={course.thumbnail}
                      alt={course.name}
                      className="course-card__thumbnail"
                    />
                  ) : (
                    <div className="course-card__thumbnail-placeholder">
                      <BookOpen size={40} strokeWidth={1.2} />
                    </div>
                  )}

                  <div className="course-card__body">
                    <h3 className="course-card__name">{course.name}</h3>

                    {progress === 100 ? (
                      <span className="course-card__completed-badge">
                        ✓ Completed
                      </span>
                    ) : (
                      <>
                        <div className="course-card__progress-label">
                          <span>Course Progress</span>
                          <strong>{progress}%</strong>
                        </div>
                        <div className="course-card__progress-bar">
                          <div
                            className="course-card__progress-fill"
                            style={{ "--progress-width": `${progress}%` }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
